import React from "react";
import { motion } from "framer-motion";
import { Activity, TrendingUp, FileText, DollarSign, EuroIcon } from "lucide-react";

function Services({ className }) {
  const services = [
    {
      title: "Real-Time Financial Insights",
      description:
        "With real-time financial data and analytics, you can stay on top of things and make prompt, certain decisions.",
      icon: Activity,
      color: "text-red-500",
    },
    {
      title: "A Strategic Approach to Finance",
      description:
        "Make use of our professional experience to develop and carry out financial strategies that promote expansion and scalability.",
      icon: TrendingUp,
      color: "text-red-500",
    },
    {
      title: "Effortless Financial Admin",
      description:
        "Payroll, invoicing, and bookkeeping are just a few of the regular tasks we'll handle so you can concentrate on your business.",
      icon: FileText,
      color: "text-red-500",
    },
    {
      title: "Affordable Financial Management",
      description:
        "Get extensive financial assistance at a reasonable cost, making the most of your investment.",
      icon: EuroIcon,
      color: "text-red-500",
    },
  ];

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { y: 30, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      className={`bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] py-16 md:py-24 relative overflow-hidden ${className}`}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-[#e2e8f0]/40"
            style={{
              width: `${Math.random() * 100 + 30}px`,
              height: `${Math.random() * 100 + 30}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="flex flex-col items-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative inline-block mb-6">
            <motion.h3
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Services
            </motion.h3>
            <motion.div
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-red-500"
              initial={{ width: 0 }}
              whileInView={{ width: "4rem" }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </div>
          <motion.p
            className="text-base md:text-lg text-gray-600 max-w-2xl text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            At AAIDAN K, we provide a set of virtual finance services that are
            specifically designed to meet the requirements of scaling startups.
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 md:p-8 transition-all duration-300 hover:border-red-500 group overflow-hidden"
              variants={item}
              whileHover={{ y: -10 }}
            >
              <div className="flex flex-col h-full">
                <motion.div
                  className="mb-4 p-3 bg-red-50 rounded-lg w-14 h-14 flex items-center justify-center group-hover:bg-red-500 transition-colors duration-300"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  <service.icon
                    className={`${service.color} w-8 h-8 group-hover:text-white transition-colors duration-300`}
                    strokeWidth={1.5}
                  />
                </motion.div>
                <h4 className="text-xl font-semibold text-gray-800 mb-3">
                  {service.title}
                </h4>
                <p className="text-gray-600 mt-auto">{service.description}</p>
                <motion.div
                  className="mt-4 pt-4 border-t border-gray-200 flex items-center"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="text-red-500 font-mono text-lg font-bold mr-2">
                    0{index + 1}
                  </div>
                  <div className="bg-red-500 h-0.5 flex-grow rounded-full"></div>
                </motion.div>
              </div>

              {/* Hover effect element */}
              <motion.div
                className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-red-100 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-red-100 blur-xl"
        animate={{
          y: [0, -15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-red-100 blur-xl"
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />
    </section>
  );
}

export default Services;
